/**
 * Receipt Service for Thaklis Pro
 *
 * TC-side wrapper around the unified Invoice Payments backend.
 * Receipts are immutable per-invoice line items of an invoice
 * payment (`invoice_payments` parent). Voiding a receipt always
 * refunds the customer wallet regardless of the original payment
 * method.
 *
 * Backend response shape per receipt row (snake_case on the wire):
 *   { id, receipt_number, payment_id, invoice_id, invoice_number,
 *     invoice_date, amount, invoice_status_after, status,
 *     void_reason, voided_at, voided_by, created_at, ... }
 *
 * Customer / TC / payment_method are NOT denormalized on the
 * receipt — read them via the parent payment (payment_id). When a
 * caller needs them at display time, this service joins on the
 * parent payment list once per page (cheap; usually < 50 receipts).
 */

import ApiService from './api.service';

const unwrap = (response) => {
  const body = response?.data;
  if (body && typeof body === 'object' && 'data' in body && 'success' in body) {
    return body.data;
  }
  return body;
};

/** Customer and user names for a typing centre (receipts and their payments only carry ids).
 *  A failed lookup gives empty maps so the receipts still show. */
const loadNames = async (typingCenterId) => {
  if (!typingCenterId) return { orgs: new Map(), users: new Map() };
  const [orgsRes, usersRes] = await Promise.all([
    ApiService.getInternalOrganizations(typingCenterId).catch(() => null),
    ApiService.getTypingCenterUsers(typingCenterId).catch(() => null),
  ]);
  const list = (res) => { const d = unwrap(res); return Array.isArray(d) ? d : (d?.content || []); };
  return {
    orgs: new Map(list(orgsRes).map((o) => [o.id, o.name])),
    users: new Map(list(usersRes).map((u) => [u.id, u.full_name ?? u.fullName ?? u.username])),
  };
};

const createdByNameOf = (r, names) => {
  const id = r.createdBy ?? r.created_by;
  if (!id) return null;
  if ((r.createdByType ?? r.created_by_type) === 'PORTAL_USER') return 'Customer (portal)';
  return names?.users.get(id) || 'Unknown user';
};

const mapReceipt = (r, parent, names) => {
  if (!r) return null;
  const orgId = parent?.internalOrganizationId ?? parent?.internal_organization_id ?? null;
  return {
    id: r.id,
    receiptNumber: r.receiptNumber ?? r.receipt_number,
    paymentId: r.paymentId ?? r.payment_id,
    invoiceId: r.invoiceId ?? r.invoice_id,
    invoiceNumber: r.invoiceNumber ?? r.invoice_number,
    invoiceDate: r.invoiceDate ?? r.invoice_date,
    amount: Number(r.amount || 0),
    amountApplied: Number(r.amount || 0),
    invoiceAmount: Number(r.invoiceAmount ?? r.invoice_amount ?? r.amount ?? 0),
    invoiceStatusAfter: r.invoiceStatusAfter ?? r.invoice_status_after,
    status: r.status,
    voidReason: r.voidReason ?? r.void_reason,
    voidedAt: r.voidedAt ?? r.voided_at,
    voidedBy: r.voidedBy ?? r.voided_by,
    createdAt: r.createdAt ?? r.created_at,
    date: r.createdAt ?? r.created_at,
    createdBy: r.createdBy ?? r.created_by,
    createdByName: createdByNameOf(r, names),
    customerName: parent?.organizationName ?? parent?.organization_name ?? (orgId && names?.orgs.get(orgId)) ?? null,
    // Joined-from-parent fields (populated when parent is supplied)
    paymentNumber: parent?.paymentNumber ?? parent?.payment_number ?? null,
    initiator: parent?.initiator ?? null,
    paymentMethod: parent?.paymentMethod ?? parent?.payment_method ?? null,
    internalOrganizationId: parent?.internalOrganizationId ?? parent?.internal_organization_id ?? null,
    typingCenterId: parent?.typingCenterId ?? parent?.typing_center_id ?? null,
  };
};

/** Fetch the parents for a list of receipts (one request per distinct
 *  payment_id) and return a Map<paymentId, parentPayment>. Used to
 *  enrich receipt rows with initiator + payment_method without
 *  requiring backend projection changes. */
const fetchParents = async (receipts) => {
  const ids = Array.from(
    new Set((receipts || []).map((r) => r.paymentId ?? r.payment_id).filter(Boolean)),
  );
  if (!ids.length) return new Map();
  const fetched = await Promise.all(
    ids.map((id) =>
      ApiService.getInvoicePayment(id)
        .then((res) => unwrap(res)?.payment ?? null)
        .catch(() => null),
    ),
  );
  const m = new Map();
  fetched.forEach((p, i) => {
    if (p) m.set(ids[i], p);
  });
  return m;
};

const ReceiptService = {
  /** List receipts for the typing center, with optional client-side
   *  filtering. Always enriches with parent-payment data so the
   *  Initiator / payment_method columns can render without a second
   *  page call. */
  getAll: async (params = {}) => {
    const typingCenterId = params.typingCenterId;
    if (!typingCenterId) return { data: [], total: 0 };
    const res = await ApiService.getReceipts(typingCenterId);
    const list = unwrap(res) || [];
    const [parents, names] = await Promise.all([fetchParents(list), loadNames(typingCenterId)]);
    let mapped = list.map((r) => mapReceipt(r, parents.get(r.paymentId ?? r.payment_id), names));

    if (params.status) {
      mapped = mapped.filter((r) => r.status === params.status);
    }
    if (params.paymentMethod) {
      mapped = mapped.filter((r) => r.paymentMethod === params.paymentMethod);
    }
    if (params.initiator) {
      mapped = mapped.filter((r) => r.initiator === params.initiator);
    }
    if (params.search) {
      const q = params.search.toLowerCase();
      mapped = mapped.filter(
        (r) =>
          (r.receiptNumber || '').toLowerCase().includes(q) ||
          (r.invoiceNumber || '').toLowerCase().includes(q) ||
          (r.customerName || '').toLowerCase().includes(q),
      );
    }
    if (params.customerId) {
      mapped = mapped.filter(
        (r) => r.internalOrganizationId === params.customerId,
      );
    }
    if (params.dateFrom) {
      mapped = mapped.filter((r) => (r.createdAt || '') >= params.dateFrom);
    }
    if (params.dateTo) {
      mapped = mapped.filter((r) => (r.createdAt || '') <= params.dateTo);
    }
    return { data: mapped, total: mapped.length };
  },

  /** Single receipt + its parent payment (for the detail page). */
  getById: async (id) => {
    const res = await ApiService.getReceiptById(id);
    const r = unwrap(res);
    if (!r) throw new Error('Receipt not found');
    let parent = null;
    const pid = r.paymentId ?? r.payment_id;
    if (pid) {
      try {
        parent = unwrap(await ApiService.getInvoicePayment(pid))
          ?.payment ?? null;
      } catch (e) {
        parent = null;
      }
    }
    const names = await loadNames(parent?.typingCenterId ?? parent?.typing_center_id);
    return mapReceipt(r, parent, names);
  },

  /** Statistics derived from the typing-center receipt list. */
  getStatistics: async (typingCenterId) => {
    const { data } = await ReceiptService.getAll({ typingCenterId });
    const active = data.filter((r) => r.status === 'ACTIVE');
    const voided = data.filter((r) => r.status === 'VOIDED');
    const now = new Date();
    const monthStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      1,
    ).toISOString();
    const activeMtd = active.filter((r) => (r.createdAt || '') >= monthStart);
    return {
      totalReceived: active.reduce((sum, r) => sum + (r.amount || 0), 0),
      receivedMtd: activeMtd.reduce((sum, r) => sum + (r.amount || 0), 0),
      receiptCount: active.length,
      voidedAmount: voided.reduce((sum, r) => sum + (r.amount || 0), 0),
    };
  },

  /** Receipts (active + voided) for a specific invoice. Used by the
   *  TC InvoiceDetail Customer Payment History block. */
  getByInvoice: async (invoiceId) => {
    if (!invoiceId) return [];
    const res = await ApiService.getReceiptsByInvoice(invoiceId);
    const list = unwrap(res) || [];
    const parents = await fetchParents(list);
    const first = parents.values().next().value;
    const names = await loadNames(first?.typingCenterId ?? first?.typing_center_id);
    return list.map((r) => mapReceipt(r, parents.get(r.paymentId ?? r.payment_id), names));
  },

  /** Void a receipt with reason. Backend always credits refund to the
   *  customer wallet (regardless of the original payment_method) and
   *  returns the impact summary. */
  void: async (id, reason) => {
    const res = await ApiService.voidReceipt(id, reason);
    return unwrap(res) || { success: true };
  },

  // ============================================================
  // HELPERS
  // ============================================================

  formatCurrency: (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  },

  // ReceiptStatus enum is now ACTIVE | VOIDED (was COMPLETED | PENDING |
  // VOIDED on the old manual-create model). Old code paths that read
  // 'COMPLETED' will not match — anywhere COMPLETED is referenced
  // should now read ACTIVE.
  getStatusColor: (status) =>
    ({
      ACTIVE: 'success',
      VOIDED: 'error',
    }[status] || 'default'),

  getStatusLabel: (status) =>
    ({
      ACTIVE: 'Active',
      VOIDED: 'Voided',
    }[status] || status),

  getPaymentMethodLabel: (method) =>
    ({
      WALLET: 'Wallet',
      CASH: 'Cash',
      BANK_TRANSFER: 'Bank Transfer',
      CHEQUE: 'Cheque',
      ONLINE: 'Online',
    }[method] || method),

  getPaymentMethodColor: (method) =>
    ({
      WALLET: 'info',
      CASH: 'success',
      BANK_TRANSFER: 'primary',
      CHEQUE: 'warning',
      ONLINE: 'info',
    }[method] || 'default'),

  // Initiator chip — replaces the old 'source' coloring helpers.
  getInitiatorLabel: (initiator) =>
    ({
      CUSTOMER: 'Customer',
      TC: 'Typing Center',
    }[initiator] || initiator),

  getInitiatorColor: (initiator) =>
    ({
      CUSTOMER: 'primary',
      TC: 'warning',
    }[initiator] || 'default'),
};

export default ReceiptService;
